/**
 * Lightweight email checks used before starting letter delivery.
 *
 * These checks intentionally validate syntax only. A domain preflight is
 * performed by the API because browser code cannot reliably inspect DNS.
 */

const EMAIL_SYNTAX_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOCAL_PART_PATTERN = /^[A-Z0-9!#$%&'*+/=?^_`{|}~.-]+$/i;
const DOMAIN_LABEL_PATTERN = /^[A-Z0-9](?:[A-Z0-9-]*[A-Z0-9])?$/i;

/** Domains whose most common misspellings are safe to suggest. */
const KNOWN_DOMAIN_TYPOS: Record<string, string> = {
  "gmial.com": "gmail.com",
  "gmal.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmail.con": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.cmo": "gmail.com",
  "hotnail.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "yaho.com": "yahoo.com",
  "yahoo.con": "yahoo.com",
  "yhoo.com": "yahoo.com",
  "outlok.com": "outlook.com",
  "outlook.con": "outlook.com",
  "icloud.con": "icloud.com",
};

export type EmailSyntaxResult = {
  valid: boolean;
  message?: string;
};

export const EMAIL_SYNTAX_REGEX = EMAIL_SYNTAX_PATTERN;

/** Validate email shape without making any network request. */
export function isValidEmailSyntax(email: string): boolean {
  const normalized = email.trim();
  if (!EMAIL_SYNTAX_PATTERN.test(normalized) || normalized.length > 254) return false;

  const atIndex = normalized.indexOf("@");
  if (atIndex <= 0 || atIndex !== normalized.lastIndexOf("@")) return false;

  const localPart = normalized.slice(0, atIndex);
  const domain = normalized.slice(atIndex + 1).toLowerCase();
  if (localPart.length > 64 || domain.length > 253) return false;
  if (
    localPart.startsWith(".") ||
    localPart.endsWith(".") ||
    localPart.includes("..") ||
    !LOCAL_PART_PATTERN.test(localPart)
  ) {
    return false;
  }

  const labels = domain.split(".");
  if (labels.length < 2 || labels.some((label) => label.length === 0 || label.length > 63)) {
    return false;
  }
  return labels.every((label) => DOMAIN_LABEL_PATTERN.test(label));
}

/** Shared naming for server-side callers that need a syntax-only gate. */
export const isValidEmailAddress = isValidEmailSyntax;

/** Alias for callers that prefer a verb-style name. */
export const validateEmailSyntax = isValidEmailSyntax;

/** Return a conservative full-address suggestion for a known domain typo. */
export function suggestEmailAddress(email: string): string | null {
  const normalized = email.trim().toLowerCase();
  const atIndex = normalized.lastIndexOf("@");
  if (atIndex <= 0 || atIndex === normalized.length - 1) return null;

  const localPart = normalized.slice(0, atIndex);
  const domain = normalized.slice(atIndex + 1);
  const suggestedDomain = KNOWN_DOMAIN_TYPOS[domain];
  if (!suggestedDomain || suggestedDomain === domain) return null;

  return `${localPart}@${suggestedDomain}`;
}

/** Alias for UI callers that want to describe the suggestion as a domain fix. */
export const getEmailDomainSuggestion = suggestEmailAddress;

export function getEmailSyntaxResult(email: string): EmailSyntaxResult {
  if (!email.trim()) {
    return { valid: false, message: "Please enter your email address." };
  }
  if (!isValidEmailSyntax(email)) {
    return { valid: false, message: "That doesn't look like a valid email." };
  }
  return { valid: true };
}
